import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/components/ui/Checkbox'
import { Field, Textarea } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { useReverseAllocation } from '@/features/finance/api/useFinance'
import { allocationLabel, isReceivableAllocation } from '@/features/finance/allocationLabel'
import { paymentRefusalMessage } from '@/features/finance/paymentRefusal'
import { useFormat } from '@/i18n/useFormat'
import { formatMoney } from '@/lib/format'
import { queryKeys } from '@/lib/query'
import type { Payment } from '@/types/domain'

/* «Отменить разнесение» (S-21): withdraw one or more of a RECEIVED payment's
 * allocations, with a reason.
 *
 * The reason is the record. The allocation row stops existing, and the audit
 * entry carrying the reason is the only place the fact survives — so it is
 * required here as it is on the server (422 `reason: required`), and the same
 * reason goes with every allocation ticked.
 *
 * Nothing is ticked when the dialog opens: what to undo is the reader's call,
 * the same way what to allocate is (AD-11).
 *
 * ONE REQUEST PER ALLOCATION, one after another, because the server reverses
 * one at a time (`POST /payments/:id/allocations/:allocationId/reverse`; a
 * batch route is out of scope). The first refusal STOPS the run: the dialog
 * stays open saying how many went through and the server's reason for the
 * next, and the payment is read again so the list behind it shows what is
 * true now. Only receivable allocations are offered — a cost line's split is
 * the carrier sheet's and the server refuses it (422 `allocationId:
 * not-receivable`). */

interface ReverseDraft {
  ticked: string[]
  reason: string
}

export function ReverseAllocationDialog({
  open,
  onClose,
  payment,
  onReversed,
}: {
  open: boolean
  onClose: () => void
  payment: Payment
  /** Handed the server's row after each reversal that lands (the Finance
   *  page's sheet keeps it). */
  onReversed?: (payment: Payment) => void
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const queryClient = useQueryClient()
  const reverse = useReverseAllocation()
  const [draft, setDraft, touched, markTouched, dirty] = useFormDraft<ReverseDraft>(
    open ? payment.id : null,
    () => ({ ticked: [], reason: '' }),
  )

  /* The run's outcome when it stopped short — reset on every open, during
     render, so a reopened dialog never greets the reader with the last one's. */
  const [stopped, setStopped] = useState<{ done: number; total: number; error: unknown } | null>(
    null,
  )
  const [running, setRunning] = useState(false)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setStopped(null)
  }

  const offered = payment.allocations.filter(isReceivableAllocation)
  const reason = draft.reason.trim()

  const submit = async () => {
    markTouched()
    if (reason === '' || draft.ticked.length === 0 || running) return
    const ids = offered.map((row) => row.id).filter((id) => draft.ticked.includes(id))
    setRunning(true)
    setStopped(null)
    let done = 0
    for (const allocationId of ids) {
      try {
        const answer = await reverse.mutateAsync({ paymentId: payment.id, allocationId, reason })
        done += 1
        onReversed?.(answer)
      } catch (error) {
        setStopped({ done, total: ids.length, error })
        setDraft((current) => ({
          ...current,
          ticked: current.ticked.filter((id) => !ids.slice(0, done).includes(id)),
        }))
        // What landed before the refusal moved the payment; read it again.
        void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
        setRunning(false)
        return
      }
    }
    setRunning(false)
    onClose()
  }

  const refusal = stopped
    ? stopped.done > 0
      ? `${t('finance.reverse.stopped', { done: stopped.done, total: stopped.total })} ${paymentRefusalMessage(stopped.error, t)}`
      : paymentRefusalMessage(stopped.error, t)
    : undefined

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('finance.reverse.title')}
      submitLabel={running ? t('finance.reverse.submitting') : t('finance.reverse.submit')}
      pending={running}
      dirty={dirty}
      blockedReason={draft.ticked.length === 0 ? t('finance.reverse.pickRequired') : undefined}
      error={
        refusal ??
        (touched && reason === '' ? t('common.form.required') : undefined)
      }
      onSubmit={() => void submit()}
    >
      <p className="text-text-muted -mt-1 text-[12.5px] font-light">
        {format.date(payment.receivedOn)} · {payment.reference} ·{' '}
        {formatMoney(payment.amount.amount, payment.amount.currency)}
      </p>

      <section className="flex flex-col gap-1.5">
        <h3 id={`reverse-what-${payment.id}`} className="asa-eyebrow">
          {t('finance.reverse.what')}
        </h3>
        <ul aria-labelledby={`reverse-what-${payment.id}`} className="flex flex-col">
          {offered.map((allocation) => (
            <li
              key={allocation.id}
              className="border-line-hair flex items-center justify-between gap-3 border-b py-2 last:border-b-0"
            >
              <Checkbox
                label={allocationLabel(t, allocation)}
                checked={draft.ticked.includes(allocation.id)}
                disabled={running}
                onCheckedChange={(on) =>
                  setDraft((current) => ({
                    ...current,
                    ticked: on
                      ? [...current.ticked, allocation.id]
                      : current.ticked.filter((id) => id !== allocation.id),
                  }))
                }
              />
              <span className="asa-num text-text-heading shrink-0 text-[13px] whitespace-nowrap">
                {formatMoney(allocation.amount.amount, allocation.amount.currency)}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <Field
        label={t('finance.reverse.reason')}
        required
        hint={t('finance.reverse.reasonHint')}
        error={touched && reason === '' ? t('common.form.required') : undefined}
      >
        {(props) => (
          <Textarea
            value={draft.reason}
            onChange={(event) =>
              setDraft((current) => ({ ...current, reason: event.target.value }))
            }
            {...props}
          />
        )}
      </Field>

      <p className="text-text-muted text-[12.5px] leading-snug font-light">
        {t('finance.reverse.consequence')}
      </p>
    </FormSheet>
  )
}
